import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/navbar/navbar';
import Sidebar from '../../components/sidebar/sidebar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    API_URLS,
    obtenerProductos,
    obtenerUsuarios
} from '../../Services/servicioURL';

interface InventarioItem {
    cve_producto: string;
    descripcion: string;
    modelo: string;
}

interface Usuario {
    id: number;
    nombre: string;
}

interface Sucursal {
    id: string;
    nombre: string;
}

interface Departamento {
    cve: string;
    nombre: string;
}

interface UltimoMovimiento {
    folio: string;
    fecha: string;
    observaciones?: string;
    referencia?: string;
    tipo: 'entrada' | 'salida' | 'mantenimiento' | 'peticion';
    estatus?: string; // Agregado para peticiones
}

interface PeticionType {
    folio: number;
    cveemp: string;
    fecha_hora: string;
    titulo: string;
    justificacion: string;
    sucursal: string;
    departamento: string;
    estatus: string; // Agregado el campo estatus
    empleado?: {
        cveemp: string;
        nomemp: string;
    };
}

const Reciente: React.FC = () => {
    const [productosCatalogo, setProductosCatalogo] = useState<InventarioItem[]>([]);
    const [usuariosCatalogo, setUsuariosCatalogo] = useState<Usuario[]>([]);
    const [sucursalesCatalogo, setSucursalesCatalogo] = useState<Sucursal[]>([]);
    const [departamentosCatalogo, setDepartamentosCatalogo] = useState<Departamento[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    // MODIFICADO: Cambiar nombres para reflejar que son TODAS las entradas/salidas/etc
    const [todasEntradas, setTodasEntradas] = useState<UltimoMovimiento[]>([]);
    const [todasSalidas, setTodasSalidas] = useState<UltimoMovimiento[]>([]);
    const [todosMantenimientos, setTodosMantenimientos] = useState<UltimoMovimiento[]>([]);
    const [todasPeticiones, setTodasPeticiones] = useState<UltimoMovimiento[]>([]);

    // Estados para los modales (AHORA SON INDEPENDIENTES)
    const [isEntradaModalVisible, setIsEntradaModalVisible] = useState<boolean>(false);
    const [selectedEntrada, setSelectedEntrada] = useState<any>(null);

    const [isSalidaModalVisible, setIsSalidaModalVisible] = useState<boolean>(false);
    const [selectedSalida, setSelectedSalida] = useState<any>(null);

    const [isMantenimientoModalVisible, setIsMantenimientoModalVisible] = useState<boolean>(false);
    const [selectedMantenimiento, setSelectedMantenimiento] = useState<any>(null);

    const [isPeticionModalVisible, setIsPeticionModalVisible] = useState<boolean>(false);
    const [selectedPeticion, setSelectedPeticion] = useState<any>(null);

    const [entradasFull, setEntradasFull] = useState<any[]>([]);
    const [salidasFull, setSalidasFull] = useState<any[]>([]);
    const [mantenimientosFull, setMantenimientosFull] = useState<any[]>([]);
    const [peticionesFull, setPeticionesFull] = useState<PeticionType[]>([]);

    const toast = useRef<Toast>(null);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
                // Cargar catálogos
                const [dataProductos, dataUsuarios, dataSucursales, dataDepartamentos] = await Promise.all([
                    obtenerProductos(API_URLS.productos.obtener),
                    obtenerUsuarios(API_URLS.usuarios.obtener),
                    fetch(API_URLS.sucursales.obtener).then(res => res.json()),
                    fetch(API_URLS.departamentos.obtener).then(res => res.json())
                ]);

                const inventarioData = dataProductos.map((producto: any) => ({
                    cve_producto: producto.cve,
                    descripcion: producto.descripcion,
                    modelo: producto.modelo
                }));
                setProductosCatalogo(inventarioData);

                const usuariosData = dataUsuarios.map((usuario: any) => ({
                    id: usuario.id,
                    nombre: usuario.nombre
                }));
                setUsuariosCatalogo(usuariosData);

                setSucursalesCatalogo(dataSucursales.map((s: any) => ({
                    id: String(s.id),
                    nombre: s.nombre
                })));

                setDepartamentosCatalogo(dataDepartamentos.map((d: any) => ({
                    cve: d.cve,
                    nombre: d.nombre
                })));

                // Cargar movimientos y peticiones
                const [dataEntradas, dataSalidas, dataMantenimientos, dataPeticiones] = await Promise.all([
                    fetch(API_URLS.entradas.obtener).then(res => res.json()),
                    fetch(API_URLS.salidas.obtener).then(res => res.json()),
                    fetch(API_URLS.mantenimientos.obtener).then(res => res.json()),
                    fetch(API_URLS.peticiones.obtener).then(res => res.json()).catch(() => [])
                ]);

                // Guardar datos completos para el modal
                setEntradasFull(dataEntradas);
                setSalidasFull(dataSalidas);
                setMantenimientosFull(dataMantenimientos);
                setPeticionesFull(dataPeticiones);

                // MODIFICADO: Procesar TODOS los movimientos
                procesarTodosLosMovimientos(dataEntradas, dataSalidas, dataMantenimientos, dataPeticiones);
            } catch (error) {
                console.error('Error al cargar datos:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudieron cargar los datos',
                    life: 3000
                });
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, []);

    // MODIFICADO: Nueva función para procesar TODOS los movimientos
    const procesarTodosLosMovimientos = (entradas: any[], salidas: any[], mantenimientos: any[], peticiones: PeticionType[]) => {
        // TODAS las entradas ordenadas por folio descendente
        const todasEntradasData = entradas
            .sort((a: any, b: any) => {
                // Ordenar por folio numérico descendente
                const folioA = parseInt(a.folio) || 0;
                const folioB = parseInt(b.folio) || 0;
                return folioB - folioA;
            })
            .map((entrada: any) => ({
                folio: entrada.folio,
                fecha: entrada.fecha,
                observaciones: entrada.observaciones,
                referencia: entrada.referencia,
                tipo: 'entrada' as const
            }));
        setTodasEntradas(todasEntradasData);

        // TODAS las salidas ordenadas por folio descendente
        const todasSalidasData = salidas
            .sort((a: any, b: any) => {
                const folioA = parseInt(a.folio) || 0;
                const folioB = parseInt(b.folio) || 0;
                return folioB - folioA;
            })
            .map((salida: any) => ({
                folio: salida.folio,
                fecha: salida.fecha,
                observaciones: salida.observaciones,
                tipo: 'salida' as const
            }));
        setTodasSalidas(todasSalidasData);

        // TODOS los mantenimientos ordenados por folio descendente
        const todosMantenimientosData = mantenimientos
            .sort((a: any, b: any) => {
                const folioA = parseInt(a.folio) || 0;
                const folioB = parseInt(b.folio) || 0;
                return folioB - folioA;
            })
            .map((mantenimiento: any) => ({
                folio: mantenimiento.folio,
                fecha: mantenimiento.fecha,
                observaciones: mantenimiento.observaciones,
                tipo: 'mantenimiento' as const
            }));
        setTodosMantenimientos(todosMantenimientosData);

        // TODAS las peticiones ordenadas por folio descendente
        const todasPeticionesData = peticiones
            .sort((a: PeticionType, b: PeticionType) => {
                return b.folio - a.folio;
            })
            .map((peticion: PeticionType) => ({
                folio: peticion.folio.toString(),
                fecha: peticion.fecha_hora,
                observaciones: peticion.titulo,
                tipo: 'peticion' as const,
                estatus: peticion.estatus
            }));
        setTodasPeticiones(todasPeticionesData);
    };

    // NUEVA FUNCIÓN: Obtener configuración del badge para estatus de peticiones
    const getEstatusBadge = (estatus: string) => {
        const estatusConfig = {
            'PENDIENTE': { severity: 'warning' as const, icon: 'pi pi-clock' },
            'EN_PROCESO': { severity: 'info' as const, icon: 'pi pi-cog' },
            'APROBADA': { severity: 'success' as const, icon: 'pi pi-check' },
            'RECHAZADA': { severity: 'danger' as const, icon: 'pi pi-times' },
            'COMPLETADA': { severity: 'success' as const, icon: 'pi pi-check-circle' },
            'CANCELADA': { severity: 'secondary' as const, icon: 'pi pi-ban' }
        };
        // Normalizar el estatus (convertir a mayúsculas y reemplazar espacios)
        const estatusNormalizado = estatus?.toUpperCase().replace(/\s+/g, '_') || 'PENDIENTE';
        return estatusConfig[estatusNormalizado as keyof typeof estatusConfig] || estatusConfig.PENDIENTE;
    };

    // FUNCIÓN MODIFICADA: Formatear estatus para mostrar
    const formatearEstatus = (estatus: string): string => {
        if (!estatus) return 'PENDIENTE';
        const estatusFormateado = estatus
            .toLowerCase()
            .replace(/_/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
        return estatusFormateado;
    };

    // Funciones para el modal
    const formatDate = (dateString?: string): string => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? '-' : date.toISOString().split('T')[0];
    };

    const formatDateDisplay = (dateString: string): string => {
        if (!dateString) return '-';
        try {
            let dateToFormat = dateString;
            if (!dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
                dateToFormat = dateString + 'T00:00:00';
            }
            const date = new Date(dateToFormat);
            if (isNaN(date.getTime())) {
                return '-';
            }
            const dateOptions: Intl.DateTimeFormatOptions = {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                timeZone: 'America/Mexico_City' 
            };
            return date.toLocaleDateString('es-ES', dateOptions);
        } catch (error) {
            console.error('Error formatting date:', error);
            return '-';
        }
    };

    const getDescripcionProducto = (cve_producto: string): string => {
        const producto = productosCatalogo.find(p => p.cve_producto === cve_producto);
        return producto ? producto.descripcion : 'Limpieza/Mantenimiento';
    };

    const getNombreTecnico = (id_tecnico: number): string => {
        const tecnico = usuariosCatalogo.find(u => u.id === id_tecnico);
        return tecnico ? tecnico.nombre : 'Técnico no encontrado';
    };

    const getNombreSucursal = (id: string | number): string => {
        const suc = sucursalesCatalogo.find(s => s.id === String(id));
        return suc ? suc.nombre : (id ? String(id) : '-');
    };

    const getNombreDepartamento = (cve: string): string => {
        const dept = departamentosCatalogo.find(d => d.cve === cve);
        return dept ? dept.nombre : (cve || '-');
    };

    // Funciones para abrir los modales (AHORA SON ESPECÍFICAS)
    const showEntradaDetail = (rowData: any) => {
        const fullData = entradasFull.find(e => e.folio === rowData.folio);
        if (fullData) {
            setSelectedEntrada(fullData);
            setIsEntradaModalVisible(true);
        }
    };

    const showSalidaDetail = (rowData: any) => {
        const fullData = salidasFull.find(s => s.folio === rowData.folio);
        if (fullData) {
            setSelectedSalida(fullData);
            setIsSalidaModalVisible(true);
        }
    };

    const showMantenimientoDetail = (rowData: any) => {
        const fullData = mantenimientosFull.find(m => m.folio === rowData.folio);
        if (fullData) {
            setSelectedMantenimiento(fullData);
            setIsMantenimientoModalVisible(true);
        }
    };

    const showPeticionDetail = (rowData: any) => {
        const fullData = peticionesFull.find(p => p.folio.toString() === rowData.folio);
        if (fullData) {
            setSelectedPeticion(fullData);
            setIsPeticionModalVisible(true);
        }
    };

    // Funciones de exportación (se mantienen igual, pero se adaptan a los nuevos estados)
    const exportToExcel = (movimiento: any, type: 'entrada' | 'salida' | 'mantenimiento' | 'peticion') => {
        if (!movimiento) return;
        if (type === 'peticion') {
            const encabezado = [
                ['Folio', movimiento.folio],
                ['Fecha', formatDate(movimiento.fecha_hora)],
                ['Empleado', movimiento.empleado?.nomemp || movimiento.cveemp],
                ['Título', movimiento.titulo],
                ['Sucursal', movimiento.sucursal],
                ['Departamento', movimiento.departamento],
                ['Estatus', formatearEstatus(movimiento.estatus)], // MODIFICADO
                ['Justificación', movimiento.justificacion]
            ];
            const ws_data: any[] = [];
            encabezado.forEach(row => ws_data.push(row));
            const worksheet = XLSX.utils.aoa_to_sheet(ws_data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Peticion');
            const fileName = `peticion_${movimiento.folio}_${formatDate(movimiento.fecha_hora)}.xlsx`;
            XLSX.writeFile(workbook, fileName);
        } else if (type === 'mantenimiento') {
            const encabezado = [
                ['Folio', movimiento.folio],
                ['Fecha', formatDate(movimiento.fecha)],
                ['Observaciones', movimiento.observaciones || '-'],
                ['Producto Principal', getDescripcionProducto(movimiento.cve_producto_principal)],
                ['Técnico', getNombreTecnico(movimiento.id_tecnico)]
            ];
            const detalle = (movimiento.mantenimientoDetalle || []).map((detalle: any) => [
                detalle.cve_producto,
                getDescripcionProducto(detalle.cve_producto),
                detalle.cantidad,
                detalle.observaciones || '-'
            ]);
            const ws_data: any[] = [];
            encabezado.forEach(row => ws_data.push(row));
            ws_data.push([]);
            ws_data.push(['Detalle:']);
            ws_data.push(['Clave', 'Descripción', 'Cantidad', 'Observación']);
            detalle.forEach((row: any) => ws_data.push(row));
            const worksheet = XLSX.utils.aoa_to_sheet(ws_data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Mantenimiento');
            const fileName = `mantenimiento_${movimiento.folio}_${formatDate(movimiento.fecha)}.xlsx`;
            XLSX.writeFile(workbook, fileName);
        } else if (type === 'salida') {
            const detalle = movimiento.salidaDetalle || [];
            const excelData = detalle.map((detalle: any) => ({
                Clave: detalle.cve_producto,
                Descripción: getDescripcionProducto(detalle.cve_producto),
                Cantidad: detalle.cantidad,
                Sucursal: getNombreSucursal(detalle.id_sucursal_destino) || '-',
                Departamento: getNombreDepartamento(detalle.cve_departamento_destino) || '-',
                Usuario: detalle.nombre_usuario || '-'
            }));
            const worksheet = XLSX.utils.json_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
            const fileName = `detalles_salida_${movimiento.folio}_${formatDate(movimiento.fecha)}.xlsx`;
            XLSX.writeFile(workbook, fileName);
        } else {
            // Entrada
            const detalle = movimiento.entradaDetalle || [];
            const excelData = detalle.map((detalle: any) => ({
                Clave: detalle.cve_producto,
                Descripción: getDescripcionProducto(detalle.cve_producto),
                Cantidad: detalle.cantidad
            }));
            const worksheet = XLSX.utils.json_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
            const fileName = `detalles_entrada_${movimiento.folio}_${formatDate(movimiento.fecha)}.xlsx`;
            XLSX.writeFile(workbook, fileName);
        }
    };

    const exportToPdf = (movimiento: any, type: 'entrada' | 'salida' | 'mantenimiento' | 'peticion') => {
        if (!movimiento) return;
        // MODIFICADO: Cambiar orientación de 'l' (landscape) a 'p' (portrait)
        const doc = new jsPDF('p', 'pt', 'a4');
        const img = new Image();
        img.src = '/dist/img/bbtt2.png';
        img.onload = function () {
            doc.addImage(img, 'PNG', 40, 20, 65, 65);
            doc.setProperties({
                title: 'Reporte de Movimientos Recientes',
                subject: `Detalles de ${type.charAt(0).toUpperCase() + type.slice(1)}`,
                author: 'Sistema de Inventarios',
                keywords: 'movimientos, reporte, pdf',
                creator: 'Sistema de Inventarios'
            });
            doc.setFontSize(18);
            doc.setTextColor(40);
            doc.text(`Detalles de ${type.charAt(0).toUpperCase() + type.slice(1)}`, 150, 40);
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Generado el: ${new Date().toLocaleDateString('es-ES')}`, 150, 60);
            let y = 100;
            if (type === 'peticion') {
                // Sección para peticiones
                doc.setFontSize(12);
                doc.text(`Folio:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${movimiento.folio}`, 75, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Fecha:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${formatDate(movimiento.fecha_hora)}`, 85, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Empleado:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${movimiento.empleado?.nomemp || movimiento.cveemp}`, 100, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Título:`, 40, y);
                doc.setFont('helvetica', 'bold');
                // Para títulos largos, usar splitTextToSize
                const tituloLines = doc.splitTextToSize(movimiento.titulo || '-', 400);
                doc.text(tituloLines, 80, y);
                doc.setFont('helvetica', 'normal');
                y += (tituloLines.length * 12) + 6;
                doc.text(`Sucursal:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${movimiento.sucursal}`, 95, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Departamento:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${movimiento.departamento}`, 130, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Estatus:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${formatearEstatus(movimiento.estatus || 'PENDIENTE')}`, 90, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Justificación:`, 40, y);
                doc.setFont('helvetica', 'bold');
                // Para justificaciones largas, usar splitTextToSize
                const justificacionLines = doc.splitTextToSize(movimiento.justificacion || '-', 400);
                doc.text(justificacionLines, 130, y);
                doc.setFont('helvetica', 'normal');
                // Agregar firma
                y += (justificacionLines.length * 12) + 40;
                doc.setFontSize(12);
                doc.setDrawColor(100);
                // MODIFICADO: Centrar línea de firma para formato vertical
                doc.line(200, y, 400, y);
                doc.text('Firma de conformidad', 300, y + 20, { align: 'center' });
                doc.save(`peticion_${movimiento.folio}_${formatDate(movimiento.fecha_hora)}.pdf`);
            } else if (type === 'mantenimiento') {
                doc.setFontSize(12);
                doc.text(`Folio:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${movimiento.folio}`, 75, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Fecha:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${formatDate(movimiento.fecha)}`, 85, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Observaciones:`, 40, y);
                doc.setFont('helvetica', 'bold');
                // MODIFICADO: Usar splitTextToSize para observaciones largas
                const observacionesLines = doc.splitTextToSize(movimiento.observaciones || '-', 400);
                doc.text(observacionesLines, 130, y);
                doc.setFont('helvetica', 'normal');
                y += (observacionesLines.length * 12) + 6;
                //blanco
                doc.text(`            `, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`           `, 90, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                //blanco
                doc.text(`Producto Principal:`, 40, y);
                doc.setFont('helvetica', 'bold');
                // MODIFICADO: Usar splitTextToSize para descripciones largas
                const productoLines = doc.splitTextToSize(getDescripcionProducto(movimiento.cve_producto_principal), 300);
                doc.text(productoLines, 145, y);
                doc.setFont('helvetica', 'normal');
                y += (productoLines.length * 12) + 6;
                //blanco 
                doc.text(`            `, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`           `, 90, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                //es donde sale el técnico
                doc.text(`Técnico:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${getNombreTecnico(movimiento.id_tecnico)}`, 90, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                const detalle = movimiento.mantenimientoDetalle || [];
                const pdfData = detalle.map((detalle: any) => [
                    detalle.cve_producto,
                    getDescripcionProducto(detalle.cve_producto),
                    detalle.cantidad,
                    detalle.observaciones || '-'
                ]);
                const autoTableResult = autoTable(doc, {
                    head: [['Clave', 'Descripción', 'Cantidad', 'Observación']],
                    body: pdfData,
                    startY: y + 20,
                    // MODIFICADO: Centrar tabla para formato vertical
                    margin: { left: 40, right: 40 },
                    tableWidth: 'auto',
                    styles: {
                        fontSize: 8,
                        cellPadding: 3,
                        overflow: 'linebreak',
                        valign: 'middle',
                        halign: 'center'
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: 255,
                        fontStyle: 'bold',
                        halign: 'center',
                        fontSize: 9
                    },
                    bodyStyles: {
                        halign: 'center',
                        valign: 'middle'
                    },
                    columnStyles: {
                        0: { cellWidth: 100, halign: 'center' },
                        1: { cellWidth: 200, halign: 'left', overflow: 'linebreak' },
                        2: { cellWidth: 60, halign: 'center' },
                        3: { cellWidth: 155, halign: 'left', overflow: 'linebreak' }
                    },
                    didDrawPage: function (data: any) {
                        const pageCount = doc.getNumberOfPages();
                        const currentPage = data.pageNumber;
                        doc.setFontSize(10);
                        doc.setTextColor(150);
                        doc.text(`Página ${currentPage} de ${pageCount}`, 40, doc.internal.pageSize.height - 20);
                    }
                });
                let finalY = (autoTableResult && autoTableResult.finalY) || (y + 290);
                doc.setFontSize(12);
                doc.setDrawColor(100);
                // MODIFICADO: Centrar línea de firma para formato vertical
                doc.line(200, finalY + 40, 400, finalY + 40);
                doc.text('Firma de conformidad', 300, finalY + 60, { align: 'center' });
                doc.save(`mantenimiento_${movimiento.folio}_${formatDate(movimiento.fecha)}.pdf`);
            } else if (type === 'salida') {
                doc.setFontSize(12);
                doc.text(`Folio:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${movimiento.folio}`, 75, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Fecha:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${formatDate(movimiento.fecha)}`, 85, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Observaciones:`, 40, y);
                doc.setFont('helvetica', 'bold');
                // MODIFICADO: Usar splitTextToSize para observaciones largas
                const observacionesLines = doc.splitTextToSize(movimiento.observaciones || '-', 400);
                doc.text(observacionesLines, 130, y);
                doc.setFont('helvetica', 'normal');
                y += (observacionesLines.length * 12) + 6;
                const tableData = movimiento.salidaDetalle || [];
                const pdfData = tableData.map((detalle: any) => [
                    detalle.cve_producto,
                    getDescripcionProducto(detalle.cve_producto),
                    detalle.cantidad,
                    getNombreSucursal(detalle.id_sucursal_destino) || '-',
                    getNombreDepartamento(detalle.cve_departamento_destino) || '-',
                    detalle.nombre_usuario || '-'
                ]);
                const autoTableResult = autoTable(doc, {
                    head: [['Clave', 'Descripción', 'Cant.', 'Sucursal', 'Departamento', 'Usuario']],
                    body: pdfData,
                    startY: y + 20,
                    // MODIFICADO: Centrar tabla para formato vertical
                    margin: { left: 40, right: 40 },
                    tableWidth: 'auto',
                    styles: {
                        fontSize: 7,
                        cellPadding: 2,
                        overflow: 'linebreak',
                        valign: 'middle',
                        halign: 'center'
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: 255,
                        fontStyle: 'bold',
                        halign: 'center',
                        fontSize: 8
                    },
                    bodyStyles: {
                        halign: 'center',
                        valign: 'middle'
                    },
                    columnStyles: {
                        0: { cellWidth: 60, halign: 'center' },
                        1: { cellWidth: 150, halign: 'left', overflow: 'linebreak' },
                        2: { cellWidth: 35, halign: 'center' },
                        3: { cellWidth: 80, halign: 'center', overflow: 'linebreak' },
                        4: { cellWidth: 80, halign: 'center', overflow: 'linebreak' },
                        5: { cellWidth: 80, halign: 'center', overflow: 'linebreak' }
                    },
                    didDrawPage: function (data: any) {
                        const pageCount = doc.getNumberOfPages();
                        const currentPage = data.pageNumber;
                        doc.setFontSize(10);
                        doc.setTextColor(150);
                        doc.text(`Página ${currentPage} de ${pageCount}`, 40, doc.internal.pageSize.height - 20);
                    }
                });
                // AGREGADO: Firma de conformidad para salidas
                let finalY = (autoTableResult && autoTableResult.finalY) || (y + 290);
                doc.setFontSize(12);
                doc.setDrawColor(100);
                // MODIFICADO: Centrar línea de firma para formato vertical
                doc.line(200, finalY + 40, 400, finalY + 40);
                doc.text('Firma de conformidad', 300, finalY + 60, { align: 'center' });
                doc.save(`detalles_salida_${movimiento.folio}_${formatDate(movimiento.fecha)}.pdf`);
            } else {
                // Entrada
                doc.setFontSize(12);
                doc.text(`Folio:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${movimiento.folio}`, 75, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Fecha:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${formatDate(movimiento.fecha)}`, 85, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Referencia:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${movimiento.referencia || '-'}`, 106, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Observaciones:`, 40, y);
                doc.setFont('helvetica', 'bold');
                // MODIFICADO: Usar splitTextToSize para observaciones largas
                const observacionesLines = doc.splitTextToSize(movimiento.observaciones || '-', 400);
                doc.text(observacionesLines, 130, y);
                doc.setFont('helvetica', 'normal');
                y += (observacionesLines.length * 12) + 6;
                const tableData = movimiento.entradaDetalle || [];
                const pdfData = tableData.map((detalle: any) => [
                    detalle.cve_producto,
                    getDescripcionProducto(detalle.cve_producto),
                    detalle.cantidad
                ]);
                const autoTableResult = autoTable(doc, {
                    head: [['Clave', 'Descripción', 'Cantidad']],
                    body: pdfData,
                    startY: y + 20,
                    // MODIFICADO: Centrar tabla para formato vertical
                    margin: { left: 40, right: 40 },
                    tableWidth: 'auto',
                    styles: {
                        fontSize: 8,
                        cellPadding: 3,
                        overflow: 'linebreak',
                        valign: 'middle',
                        halign: 'center'
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: 255,
                        fontStyle: 'bold',
                        halign: 'center',
                        fontSize: 9
                    },
                    bodyStyles: {
                        halign: 'center',
                        valign: 'middle'
                    },
                    columnStyles: {
                        0: { cellWidth: 120, halign: 'center' },
                        1: { cellWidth: 280, halign: 'left', overflow: 'linebreak' },
                        2: { cellWidth: 85, halign: 'center' }
                    },
                    didDrawPage: function (data: any) {
                        const pageCount = doc.getNumberOfPages();
                        const currentPage = data.pageNumber;
                        doc.setFontSize(10);
                        doc.setTextColor(150);
                        doc.text(`Página ${currentPage} de ${pageCount}`, 40, doc.internal.pageSize.height - 20);
                    }
                });
                // AGREGADO: Firma de conformidad para entradas también
                let finalY = (autoTableResult && autoTableResult.finalY) || (y + 290);
                doc.setFontSize(12);
                doc.setDrawColor(100);
                // MODIFICADO: Centrar línea de firma para formato vertical
                doc.line(200, finalY + 40, 400, finalY + 40);
                doc.text('Firma de conformidad', 300, finalY + 60, { align: 'center' });
                doc.save(`detalles_entrada_${movimiento.folio}_${formatDate(movimiento.fecha)}.pdf`);
            }
        };
    };

    const getTipoBadge = (tipo: string) => {
        const config = {
            entrada: { severity: 'success' as const, icon: 'pi pi-arrow-up' },
            salida: { severity: 'danger' as const, icon: 'pi pi-arrow-down' },
            mantenimiento: { severity: 'warning' as const, icon: 'pi pi-wrench' },
            peticion: { severity: 'info' as const, icon: 'pi pi-file-edit' }
        };
        return config[tipo as keyof typeof config] || config.entrada;
    };

    if (loading) {
        return (
            <>
                <Toast ref={toast} />
                <Navbar />
                <Sidebar />
                <div className="content-wrapper" style={{
                    backgroundImage: 'url(/dist/img/FondoLogin.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundAttachment: 'fixed',
                    minHeight: '100vh',
                    position: 'relative'
                }}>
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.4)',
                        zIndex: 1
                    }}></div>
                    <div style={{ position: 'relative', zIndex: 2 }}>
                        <div className="content-header">
                            <div className="container-fluid">
                                <div className="row mb-2">
                                    <div className="col-sm-6">
                                        <h1 className="m-0">Todos los Movimientos</h1>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <section className="content">
                            <div className="container-fluid">
                                <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                                    <div className="text-center">
                                        <i className="fas fa-spinner fa-spin fa-3x text-primary mb-3"></i>
                                        <h4>Cargando todos los movimientos...</h4>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Toast ref={toast} />
            <Navbar />
            <Sidebar />
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative'
            }}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.4)',
                    zIndex: 1
                }}></div>
                <div style={{ position: 'relative', zIndex: 2 }}>
                    <div className="content-header">
                        <div className="container-fluid">
                            <div className="row mb-2">
                                <div className="col-sm-6">
                                    {/* MODIFICADO: Cambiar título */}
                                    <h1 className="m-0">Todos los Movimientos</h1>
                                </div>
                                <div className="col-sm-6">
                                    <ol className="breadcrumb float-sm-right">
                                        <li className="breadcrumb-item"><a href="/inicio">Dashboard</a></li>
                                        <li className="breadcrumb-item active">Todos los Movimientos</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                    <section className="content">
                        <div className="container-fluid">
                            {/* MODIFICADO: Cambiar títulos de las tarjetas */}
                            <div className="row mb-4">
                                <div className="col-lg-3 col-md-6 mb-4">
                                    <Card title={`Todas las Entradas (${todasEntradas.length})`} className="h-100" style={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        <DataTable 
                                            value={todasEntradas} 
                                            size="small"
                                            emptyMessage="No hay entradas registradas"
                                            onRowClick={(e) => showEntradaDetail(e.data)}
                                            rowHover
                                            style={{ cursor: 'pointer' }}
                                            paginator
                                            rows={10}
                                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                                            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} entradas"
                                        >
                                            <Column 
                                                field="folio" 
                                                header="Folio"
                                                style={{ width: '80px' }}
                                            />
                                            <Column 
                                                field="fecha" 
                                                header="Fecha"
                                                body={(rowData) => formatDateDisplay(rowData.fecha)}
                                                style={{ width: '100px' }}
                                            />
                                            <Column 
                                                field="tipo" 
                                                header="Tipo"
                                                body={(rowData) => {
                                                    const config = getTipoBadge(rowData.tipo);
                                                    return (
                                                        <Badge 
                                                            value={rowData.tipo.toUpperCase()} 
                                                            severity={config.severity}
                                                        />
                                                    );
                                                }}
                                                style={{ width: '80px' }}
                                            />
                                        </DataTable>
                                    </Card>
                                </div>
                                <div className="col-lg-3 col-md-6 mb-4">
                                    <Card title={`Todas las Salidas (${todasSalidas.length})`} className="h-100" style={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        <DataTable 
                                            value={todasSalidas} 
                                            size="small"
                                            emptyMessage="No hay salidas registradas"
                                            onRowClick={(e) => showSalidaDetail(e.data)}
                                            rowHover
                                            style={{ cursor: 'pointer' }}
                                            paginator
                                            rows={10}
                                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                                            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} salidas"
                                        >
                                            <Column 
                                                field="folio" 
                                                header="Folio"
                                                style={{ width: '80px' }}
                                            />
                                            <Column 
                                                field="fecha" 
                                                header="Fecha"
                                                body={(rowData) => formatDateDisplay(rowData.fecha)}
                                                style={{ width: '100px' }}
                                            />
                                            <Column 
                                                field="tipo" 
                                                header="Tipo"
                                                body={(rowData) => {
                                                    const config = getTipoBadge(rowData.tipo);
                                                    return (
                                                        <Badge 
                                                            value={rowData.tipo.toUpperCase()} 
                                                            severity={config.severity}
                                                        />
                                                    );
                                                }}
                                                style={{ width: '80px' }}
                                            />
                                        </DataTable>
                                    </Card>
                                </div>
                                <div className="col-lg-3 col-md-6 mb-4">
                                    <Card title={`Todos los Mantenimientos (${todosMantenimientos.length})`} className="h-100" style={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        <DataTable 
                                            value={todosMantenimientos} 
                                            size="small"
                                            emptyMessage="No hay mantenimientos registrados"
                                            onRowClick={(e) => showMantenimientoDetail(e.data)}
                                            rowHover
                                            style={{ cursor: 'pointer' }}
                                            paginator
                                            rows={10}
                                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                                            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} mantenimientos"
                                        >
                                            <Column 
                                                field="folio" 
                                                header="Folio"
                                                style={{ width: '80px' }}
                                            />
                                            <Column 
                                                field="fecha" 
                                                header="Fecha"
                                                body={(rowData) => formatDateDisplay(rowData.fecha)}
                                                style={{ width: '100px' }}
                                            />
                                            <Column 
                                                field="tipo" 
                                                header="Tipo"
                                                body={(rowData) => {
                                                    const config = getTipoBadge(rowData.tipo);
                                                    return (
                                                        <Badge 
                                                            value={rowData.tipo.toUpperCase()} 
                                                            severity={config.severity}
                                                        />
                                                    );
                                                }}
                                                style={{ width: '80px' }}
                                            />
                                        </DataTable>
                                    </Card>
                                </div>
                                <div className="col-lg-3 col-md-6 mb-4">
                                    <Card title={`Todas las Peticiones (${todasPeticiones.length})`} className="h-100" style={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        <DataTable 
                                            value={todasPeticiones} 
                                            size="small"
                                            emptyMessage="No hay peticiones registradas"
                                            onRowClick={(e) => showPeticionDetail(e.data)}
                                            rowHover
                                            style={{ cursor: 'pointer' }}
                                            paginator
                                            rows={10}
                                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                                            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} peticiones"
                                        >
                                            <Column 
                                                field="folio" 
                                                header="Folio"
                                                style={{ width: '80px' }}
                                            />
                                            <Column 
                                                field="fecha" 
                                                header="Fecha"
                                                body={(rowData) => formatDateDisplay(rowData.fecha)}
                                                style={{ width: '100px' }}
                                            />
                                            <Column 
                                                field="estatus" 
                                                header="Estatus"
                                                body={(rowData) => {
                                                    const config = getEstatusBadge(rowData.estatus || 'PENDIENTE');
                                                    return (
                                                        <Badge 
                                                            value={formatearEstatus(rowData.estatus || 'PENDIENTE')} 
                                                            severity={config.severity}
                                                        />
                                                    );
                                                }}
                                                style={{ width: '100px' }}
                                            />
                                        </DataTable>
                                    </Card>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {/* MODAL PARA ENTRADAS */}
            <Dialog
                header="Detalles de Entrada"
                visible={isEntradaModalVisible}
                style={{ width: '85vw', maxWidth: '1200px' }}
                onHide={() => setIsEntradaModalVisible(false)}
                resizable
                draggable
            >
                {selectedEntrada && (
                    <div className="p-fluid">
                        <div className="card">
                            <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', padding: '1rem' }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="m-0">Encabezado de Entrada</h5>
                                    <div>
                                        <button
                                            onClick={() => exportToExcel(selectedEntrada, 'entrada')}
                                            className="btn btn-sm btn-success mr-2"
                                            title="Exportar a Excel"
                                        >
                                            <i className="pi pi-file-excel"></i>
                                        </button>
                                        <button
                                            onClick={() => exportToPdf(selectedEntrada, 'entrada')}
                                            className="btn btn-sm btn-danger"
                                            title="Exportar a PDF"
                                        >
                                            <i className="pi pi-file-pdf"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Folio:</label>
                                            <p className="form-control-plaintext">{selectedEntrada.folio}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Fecha:</label>
                                            <p className="form-control-plaintext">{formatDate(selectedEntrada.fecha)}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Referencia:</label>
                                            <p className="form-control-plaintext">{selectedEntrada.referencia || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Observaciones:</label>
                                            <p className="form-control-plaintext">{selectedEntrada.observaciones || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card mt-4">
                            <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', padding: '1rem' }}>
                                <h5 className="m-0">Detalle de la Entrada</h5>
                            </div>
                            <div className="card-body p-0">
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <table className="table table-bordered table-striped mb-0">
                                        <thead className="thead-light">
                                            <tr>
                                                <th style={{ width: '15%', textAlign: 'center' }}>Clave</th>
                                                <th style={{ width: '65%', textAlign: 'left' }}>Descripción</th>
                                                <th style={{ width: '20%', textAlign: 'center' }}>Cantidad</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedEntrada.entradaDetalle && selectedEntrada.entradaDetalle.length > 0 ? (
                                                selectedEntrada.entradaDetalle.map((detalle: any, index: number) => (
                                                    <tr key={index}>
                                                        <td style={{ textAlign: 'center', fontWeight: '500', backgroundColor: '#f8f9fa' }}>
                                                            {detalle.cve_producto}
                                                        </td>
                                                        <td style={{ textAlign: 'left', paddingLeft: '1rem' }}>
                                                            {getDescripcionProducto(detalle.cve_producto)}
                                                        </td>
                                                        <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#007bff' }}>
                                                            {detalle.cantidad}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={3} style={{ textAlign: 'center', padding: '20px', fontStyle: 'italic', color: '#6c757d' }}>
                                                        No hay detalles disponibles
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mt-4">
                            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                                _____________________________
                            </div>
                            <div style={{ fontWeight: 'bold', letterSpacing: '2px' }}>
                                Firma de conformidad
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            {/* MODAL PARA SALIDAS */}
            <Dialog
                header="Detalles de Salida"
                visible={isSalidaModalVisible}
                style={{ width: '85vw', maxWidth: '1200px' }}
                onHide={() => setIsSalidaModalVisible(false)}
                resizable
                draggable
            >
                {selectedSalida && (
                    <div className="p-fluid">
                        <div className="card">
                            <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', padding: '1rem' }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="m-0">Encabezado de Salida</h5>
                                    <div>
                                        <button
                                            onClick={() => exportToExcel(selectedSalida, 'salida')}
                                            className="btn btn-sm btn-success mr-2"
                                            title="Exportar a Excel"
                                        >
                                            <i className="pi pi-file-excel"></i>
                                        </button>
                                        <button
                                            onClick={() => exportToPdf(selectedSalida, 'salida')}
                                            className="btn btn-sm btn-danger"
                                            title="Exportar a PDF"
                                        >
                                            <i className="pi pi-file-pdf"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Folio:</label>
                                            <p className="form-control-plaintext">{selectedSalida.folio}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Fecha:</label>
                                            <p className="form-control-plaintext">{formatDate(selectedSalida.fecha)}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Observaciones:</label>
                                            <p className="form-control-plaintext">{selectedSalida.observaciones || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Sucursal Destino:</label>
                                            <p className="form-control-plaintext">
                                                {Array.isArray(selectedSalida.salidaDetalle) && selectedSalida.salidaDetalle.length > 0
                                                    ? Array.from(new Set(selectedSalida.salidaDetalle.map((d: any) => getNombreSucursal(d.id_sucursal_destino)))).join(', ')
                                                    : '-'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Departamento Destino:</label>
                                            <p className="form-control-plaintext">
                                                {Array.isArray(selectedSalida.salidaDetalle) && selectedSalida.salidaDetalle.length > 0
                                                    ? Array.from(new Set(selectedSalida.salidaDetalle.map((d: any) => getNombreDepartamento(d.cve_departamento_destino)))).join(', ')
                                                    : '-'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card mt-4">
                            <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', padding: '1rem' }}>
                                <h5 className="m-0">Detalle de la Salida</h5>
                            </div>
                            <div className="card-body p-0">
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <table className="table table-bordered table-striped mb-0">
                                        <thead className="thead-light">
                                            <tr>
                                                <th style={{ width: '12%', textAlign: 'center' }}>Clave</th>
                                                <th style={{ width: '35%', textAlign: 'left' }}>Descripción</th>
                                                <th style={{ width: '10%', textAlign: 'center' }}>Cant.</th>
                                                <th style={{ width: '15%', textAlign: 'center' }}>Sucursal</th>
                                                <th style={{ width: '15%', textAlign: 'center' }}>Departamento</th>
                                                <th style={{ width: '13%', textAlign: 'center' }}>Usuario</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedSalida.salidaDetalle && selectedSalida.salidaDetalle.length > 0 ? (
                                                selectedSalida.salidaDetalle.map((detalle: any, index: number) => (
                                                    <tr key={index}>
                                                        <td style={{ textAlign: 'center', fontWeight: '500', backgroundColor: '#f8f9fa' }}>
                                                            {detalle.cve_producto}
                                                        </td>
                                                        <td style={{ textAlign: 'left', paddingLeft: '1rem' }}>
                                                            {getDescripcionProducto(detalle.cve_producto)}
                                                        </td>
                                                        <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#007bff' }}>
                                                            {detalle.cantidad}
                                                        </td>
                                                        <td style={{ textAlign: 'center', fontSize: '0.85rem' }}>
                                                            {getNombreSucursal(detalle.id_sucursal_destino) || '-'}
                                                        </td>
                                                        <td style={{ textAlign: 'center', fontSize: '0.85rem' }}>
                                                            {getNombreDepartamento(detalle.cve_departamento_destino) || '-'}
                                                        </td>
                                                        <td style={{ textAlign: 'center', fontWeight: '500', color: '#28a745' }}>
                                                            {detalle.nombre_usuario || '-'}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={6} style={{ textAlign: 'center', padding: '20px', fontStyle: 'italic', color: '#6c757d' }}>
                                                        No hay detalles disponibles
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mt-4">
                            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                                _____________________________
                            </div>
                            <div style={{ fontWeight: 'bold', letterSpacing: '2px' }}>
                                Firma de conformidad
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            {/* MODAL PARA MANTENIMIENTOS */}
            <Dialog
                header="Detalles de Mantenimiento"
                visible={isMantenimientoModalVisible}
                style={{ width: '85vw', maxWidth: '1200px' }}
                onHide={() => setIsMantenimientoModalVisible(false)}
                resizable
                draggable
            >
                {selectedMantenimiento && (
                    <div className="p-fluid">
                        <div className="card">
                            <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', padding: '1rem' }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="m-0">Encabezado de Mantenimiento</h5>
                                    <div>
                                        <button
                                            onClick={() => exportToExcel(selectedMantenimiento, 'mantenimiento')}
                                            className="btn btn-sm btn-success mr-2"
                                            title="Exportar a Excel"
                                        >
                                            <i className="pi pi-file-excel"></i>
                                        </button>
                                        <button
                                            onClick={() => exportToPdf(selectedMantenimiento, 'mantenimiento')}
                                            className="btn btn-sm btn-danger"
                                            title="Exportar a PDF"
                                        >
                                            <i className="pi pi-file-pdf"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Folio:</label>
                                            <p className="form-control-plaintext">{selectedMantenimiento.folio}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Fecha:</label>
                                            <p className="form-control-plaintext">{formatDate(selectedMantenimiento.fecha)}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Técnico:</label>
                                            <p className="form-control-plaintext">{getNombreTecnico(selectedMantenimiento.id_tecnico)}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Producto Principal:</label>
                                            <p className="form-control-plaintext">{getDescripcionProducto(selectedMantenimiento.cve_producto_principal)}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Observaciones:</label>
                                            <p className="form-control-plaintext">{selectedMantenimiento.observaciones || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card mt-4">
                            <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', padding: '1rem' }}>
                                <h5 className="m-0">Detalle del Mantenimiento</h5>
                            </div>
                            <div className="card-body p-0">
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <table className="table table-bordered table-striped mb-0">
                                        <thead className="thead-light">
                                            <tr>
                                                <th style={{ width: '15%', textAlign: 'center' }}>Clave</th>
                                                <th style={{ width: '40%', textAlign: 'left' }}>Descripción</th>
                                                <th style={{ width: '15%', textAlign: 'center' }}>Cantidad</th>
                                                <th style={{ width: '30%', textAlign: 'left' }}>Observación</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedMantenimiento.mantenimientoDetalle && selectedMantenimiento.mantenimientoDetalle.length > 0 ? (
                                                selectedMantenimiento.mantenimientoDetalle.map((detalle: any, index: number) => (
                                                    <tr key={index}>
                                                        <td style={{ textAlign: 'center', fontWeight: '500', backgroundColor: '#f8f9fa' }}>
                                                            {detalle.cve_producto}
                                                        </td>
                                                        <td style={{ textAlign: 'left', paddingLeft: '1rem' }}>
                                                            {getDescripcionProducto(detalle.cve_producto)}
                                                        </td>
                                                        <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#007bff' }}>
                                                            {detalle.cantidad}
                                                        </td>
                                                        <td style={{ textAlign: 'left', paddingLeft: '1rem', fontStyle: detalle.observaciones ? 'normal' : 'italic', color: detalle.observaciones ? 'inherit' : '#6c757d' }}>
                                                            {detalle.observaciones || '-'}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={4} style={{ textAlign: 'center', padding: '20px', fontStyle: 'italic', color: '#6c757d' }}>
                                                        No hay detalles disponibles
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mt-4">
                            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                                _____________________________
                            </div>
                            <div style={{ fontWeight: 'bold', letterSpacing: '2px' }}>
                                Firma de conformidad
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            {/* MODAL PARA PETICIONES */}
            <Dialog
                header="Detalles de Petición"
                visible={isPeticionModalVisible}
                style={{ width: '85vw', maxWidth: '1200px' }}
                onHide={() => setIsPeticionModalVisible(false)}
                resizable
                draggable
            >
                {selectedPeticion && (
                    <div className="p-fluid">
                        <div className="card">
                            <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', padding: '1rem' }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="m-0">Encabezado de Petición</h5>
                                    <div>
                                        <button
                                            onClick={() => exportToExcel(selectedPeticion, 'peticion')}
                                            className="btn btn-sm btn-success mr-2"
                                            title="Exportar a Excel"
                                        >
                                            <i className="pi pi-file-excel"></i>
                                        </button>
                                        <button
                                            onClick={() => exportToPdf(selectedPeticion, 'peticion')}
                                            className="btn btn-sm btn-danger"
                                            title="Exportar a PDF"
                                        >
                                            <i className="pi pi-file-pdf"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Folio:</label>
                                            <p className="form-control-plaintext">{selectedPeticion.folio}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Fecha:</label>
                                            <p className="form-control-plaintext">{formatDate(selectedPeticion.fecha_hora)}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Empleado:</label>
                                            <p className="form-control-plaintext">{selectedPeticion.empleado?.nomemp || selectedPeticion.cveemp}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Título:</label>
                                            <p className="form-control-plaintext">{selectedPeticion.titulo}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Sucursal:</label>
                                            <p className="form-control-plaintext">{selectedPeticion.sucursal}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Departamento:</label>
                                            <p className="form-control-plaintext">{selectedPeticion.departamento}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Estatus:</label>
                                            <div className="mt-2">
                                                <Badge 
                                                    value={formatearEstatus(selectedPeticion.estatus || 'PENDIENTE')} 
                                                    severity={getEstatusBadge(selectedPeticion.estatus || 'PENDIENTE').severity}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-12">
                                        <div className="form-group">
                                            <label className="font-weight-bold">Justificación:</label>
                                            <p className="form-control-plaintext" style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f8f9fa', padding: '0.5rem', borderRadius: '4px' }}>
                                                {selectedPeticion.justificacion || '-'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mt-4">
                            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                                _____________________________
                            </div>
                            <div style={{ fontWeight: 'bold', letterSpacing: '2px' }}>
                                Firma de conformidad
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            {/* Estilos CSS adicionales */}
            <style>
                {`
                    .p-datatable .p-datatable-tbody > tr {
                        cursor: pointer;
                        transition: background-color 0.2s ease;
                    }
                    .p-datatable .p-datatable-tbody > tr:hover {
                        background-color: #f8f9fa !important;
                    }
                    @media (max-width: 768px) {
                        .content-wrapper {
                            background-attachment: scroll !important;
                        }
                        .p-dialog {
                            width: 95vw !important;
                        }
                    }
                    /* Estilos para mejorar la visualización de los modales */
                    .p-dialog .p-dialog-content {
                        padding: 1.5rem !important;
                    }
                    .form-control-plaintext {
                        padding: 0.375rem 0;
                        margin-bottom: 0;
                        line-height: 1.5;
                        border: none;
                        border-radius: 0;
                        background-color: transparent;
                    }
                    .card {
                        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                        border-radius: 8px;
                        margin-bottom: 1rem;
                    }
                    .card-header {
                        border-radius: 8px 8px 0 0 !important;
                    }
                    table.table {
                        margin-bottom: 0;
                    }
                    table.table th {
                        padding: 0.75rem;
                        vertical-align: middle;
                    }
                    table.table td {
                        padding: 0.75rem;
                        vertical-align: middle;
                    }
                `}
            </style>
        </>
    );
};

export default Reciente;